import { formatDateRange } from '@/utils';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import { useGetOrgDisciplineStatsQuery } from '../../../../../../app/features/employee/management-dashboard/management-dashboard.api';
import CustomGaugeChart from '../../../../../../components/Chart/CustomGaugeChart';
import CustomRangePicker from '../../../../../../components/CustomRangePicker';
import useError from '../../../../../../hooks/useError';
import type { ManagementFilters } from '../../../../../../hooks/useManagementFilters';

const GAUGE_SEGMENTS = [
  {
    label: 'Very Poor',
    value: 20,
    color: '#c94a6a',
    gradientId: 'grad-very-poor',
    stops: ['#752b3e', '#e66b8b'] as [string, string],
  },
  {
    label: 'Poor',
    value: 20,
    color: '#f2a65a',
    gradientId: 'grad-poor',
    stops: ['#9c6b3a', '#ffbf7a'] as [string, string],
  },
  {
    label: 'Fair',
    value: 20,
    color: '#d6b85a',
    gradientId: 'grad-fair',
    stops: ['#8a763a', '#ebd17a'] as [string, string],
  },
  {
    label: 'Good',
    value: 20,
    color: '#55cfc0',
    gradientId: 'grad-good',
    stops: ['#37877d', '#55cfc0'] as [string, string],
  },
  {
    label: 'Excellent',
    value: 20,
    color: '#5ee07a',
    gradientId: 'grad-excellent',
    stops: ['#3d914f', '#80fc9c'] as [string, string],
  },
];

const WorkDisciplineScore = ({
  department_id,
  office_id,
  filter: allFilters,
  setFilter,
  isFilterChanged,
  resetFilter,
}: {
  department_id?: string;
  office_id?: string;
  filter: ManagementFilters;
  setFilter: (key: any, value: any) => void;
  isFilterChanged: (key: any) => boolean;
  resetFilter: (key: any) => void;
}) => {
  const filter = allFilters.workDiscipline;

  const { data: statsData, error } = useGetOrgDisciplineStatsQuery({
    start_date: filter.start_date || '',
    end_date: filter.end_date || '',
    department_id,
    office_id,
  });
  useError(error, 'Failed to fetch work discipline stats');

  const stats = statsData?.data?.data;
  const score = stats?.average_wds || 0;
  const statusLabel = stats?.status || '--';

  return (
    <div className="bg-theme-slate-900 border border-theme-gray-700 p-4 rounded-xl w-full space-y-4 relative">
      <Flex
        justify="space-between"
        align="start"
        className="!z-[10] absolute left-5 right-5 top-5"
      >
        <h4 className="text-lg px-3 py-1 rounded border border-theme-gray-700 text-white leading-tight">
          Work Discipline Score
          <span className="text-theme-primary block text-sm">
            {formatDateRange(filter.start_date, filter.end_date)}
          </span>
        </h4>
        <CustomRangePicker
          startDate={filter.start_date}
          endDate={filter.end_date}
          onChange={(start, end) => {
            setFilter('workDiscipline', { start_date: start, end_date: end });
          }}
          disabledDate={(current) =>
            current > dayjs().subtract(1, 'day').endOf('day')
          }
          isChanged={isFilterChanged('workDiscipline')}
          onReset={() => resetFilter('workDiscipline')}
        />
      </Flex>
      <div className="h-[70px]" />
      {/* Reusable Gauge Chart */}
      <CustomGaugeChart
        height={320}
        score={score}
        segments={GAUGE_SEGMENTS}
        statusLabel={statusLabel}
        showStatusInTooltip={false}
      />
    </div>
  );
};

export default WorkDisciplineScore;
